import { useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Crown,
  TrendingUp,
  Zap,
  MessageSquare,
  Target,
  Brain,
  ArrowRight,
  Play,
  Loader2,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { topicsData } from '../data/siteData';
import { useBlogPosts, useVideos } from '../hooks/useContent';

const iconMap = {
  Crown: Crown,
  TrendingUp: TrendingUp,
  Zap: Zap,
  MessageSquare: MessageSquare,
  Target: Target,
  Brain: Brain,
};

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTopic = searchParams.get('topic') || null;
  const [activeTopic, setActiveTopic] = useState(initialTopic);
  
  // Fetch content from Contentful
  const { blogPosts = [], isLoading: postsLoading } = useBlogPosts();
  const { videos = [], isLoading: videosLoading } = useVideos();
  
  const isLoading = postsLoading || videosLoading;

  const handleTopicChange = (topicId) => {
    if (topicId === activeTopic) {
      setActiveTopic(null);
      searchParams.delete('topic');
    } else {
      setActiveTopic(topicId);
      searchParams.set('topic', topicId);
    }
    setSearchParams(searchParams);
  };

  const filteredContent = useMemo(() => {
    const safeBlogPosts = Array.isArray(blogPosts) ? blogPosts : [];
    const safeVideos = Array.isArray(videos) ? videos : [];
    
    if (!activeTopic) {
      return {
        blogs: safeBlogPosts.slice(0, 6),
        videos: safeVideos.slice(0, 4),
      };
    }
    
    // Filter by topic tag
    return {
      blogs: safeBlogPosts.filter((post) => 
        post.topic === activeTopic || 
        post.tags?.some(tag => tag.toLowerCase() === activeTopic.toLowerCase())
      ),
      videos: safeVideos.filter((video) => 
        video.tags?.some(tag => tag.toLowerCase() === activeTopic.toLowerCase())
      ),
    };
  }, [activeTopic, blogPosts, videos]);

  const selectedTopicData = topicsData.find((t) => t.id === activeTopic);

  return (
    <div data-testid="topics-page">
      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-brand-accent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <p className="text-brand-secondary font-semibold tracking-widest uppercase text-sm mb-4">
              Explore
            </p>
            <h1 className="font-heading text-4xl md:text-6xl font-bold text-brand-primary mb-6">
              Topics I Cover
            </h1>
            <p className="text-slate-600 text-lg">
              Dive into specific topics that interest you. From leadership and strategy to mindset
              and communication, explore content tailored to your growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Topics Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {topicsData.map((topic, index) => {
              const IconComponent = iconMap[topic.icon] || Crown;
              const isActive = activeTopic === topic.id;
              return (
                <motion.button
                  key={topic.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  onClick={() => handleTopicChange(topic.id)}
                  className={`p-4 rounded-2xl text-center transition-all duration-300 ${
                    isActive
                      ? 'text-white shadow-lg scale-105'
                      : 'bg-slate-50 text-brand-primary hover:bg-slate-100'
                  }`}
                  style={{
                    backgroundColor: isActive ? topic.color : undefined,
                  }}
                  data-testid={`topic-filter-${topic.id}`}
                >
                  <IconComponent
                    className={`w-8 h-8 mx-auto mb-2 ${
                      isActive ? 'text-white' : 'text-slate-600'
                    }`}
                  />
                  <span className="font-medium text-sm">{topic.name}</span>
                </motion.button>
              );
            })}
          </div>

          {activeTopic && selectedTopicData && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 text-center"
            >
              <p className="text-slate-600">{selectedTopicData.description}</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleTopicChange(activeTopic)}
                className="mt-2 text-brand-secondary"
              >
                Clear Filter
              </Button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Content Section */}
      <section className="py-16 md:py-24 bg-brand-accent">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-brand-secondary" />
            </div>
          ) : (
            <>
              {/* Blog Posts */}
              {filteredContent.blogs.length > 0 && (
                <div className="mb-16">
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-primary">
                      Articles {activeTopic && `on ${selectedTopicData?.name}`}
                    </h2>
                    <Link
                      to="/blog"
                      className="text-brand-secondary font-medium flex items-center hover:gap-2 transition-all"
                    >
                      View All <ArrowRight className="ml-1 w-4 h-4" />
                    </Link>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredContent.blogs.map((post, index) => (
                      <motion.article
                        key={post.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="group"
                      >
                        <Link to={`/blog/${post.slug}`} className="block">
                          <div className="bg-white rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                            <div className="relative aspect-[4/3] overflow-hidden">
                              <img
                                src={post.image}
                                alt={post.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                            </div>
                            <div className="p-6">
                              <div className="flex gap-2 mb-3">
                                {(post.tags || []).slice(0, 2).map((tag) => (
                                  <span
                                    key={tag}
                                    className="text-xs font-medium text-brand-secondary bg-brand-secondary/10 px-3 py-1 rounded-full"
                                  >
                                    {tag}
                                  </span>
                                ))}
                              </div>
                              <h3 className="font-heading text-lg font-bold text-brand-primary mb-2 group-hover:text-brand-secondary transition-colors line-clamp-2">
                                {post.title}
                              </h3>
                              <p className="text-slate-600 text-sm line-clamp-2">{post.excerpt}</p>
                              <div className="mt-4 text-xs text-slate-500">
                                {post.date} • {post.readTime}
                              </div>
                            </div>
                          </div>
                        </Link>
                      </motion.article>
                    ))}
                  </div>
                </div>
              )}

              {/* Videos */}
              {filteredContent.videos.length > 0 && (
                <div>
                  <div className="flex items-center justify-between mb-8">
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-brand-primary">
                      Videos {activeTopic && `on ${selectedTopicData?.name}`}
                    </h2>
                    <Link
                      to="/videos"
                      className="text-brand-secondary font-medium flex items-center hover:gap-2 transition-all"
                    >
                      View All <ArrowRight className="ml-1 w-4 h-4" />
                    </Link>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {filteredContent.videos.slice(0, 4).map((video, index) => (
                      <motion.div
                        key={video.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                        className="group"
                      >
                        <Link to={`/videos/${video.slug}`} className="block">
                          <div className="bg-white rounded-2xl overflow-hidden hover:shadow-xl transition-all duration-500 hover:-translate-y-1">
                            <div className="relative aspect-video overflow-hidden">
                              <img
                                src={video.thumbnail}
                                alt={video.title}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              />
                              <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                <div className="w-14 h-14 rounded-full bg-white flex items-center justify-center">
                                  <Play className="w-5 h-5 text-brand-primary ml-1" />
                                </div>
                              </div>
                              <span className="absolute top-4 left-4 bg-brand-secondary text-white text-xs font-semibold px-3 py-1 rounded-full uppercase">
                                {video.category}
                              </span>
                              <span className="absolute bottom-4 right-4 bg-black/70 text-white text-xs px-2 py-1 rounded">
                                {video.duration}
                              </span>
                            </div>
                            <div className="p-6">
                              <h3 className="font-heading text-lg font-bold text-brand-primary group-hover:text-brand-secondary transition-colors">
                                {video.title}
                              </h3>
                              <p className="text-slate-600 text-sm mt-2 line-clamp-2">
                                {video.description}
                              </p>
                            </div>
                          </div>
                        </Link>
                      </motion.div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Content */}
              {filteredContent.blogs.length === 0 && filteredContent.videos.length === 0 && (
                <div className="text-center py-16 bg-white rounded-2xl">
                  <p className="text-slate-600 text-lg mb-2">
                    {activeTopic ? `No content found for "${selectedTopicData?.name}" yet.` : 'No content available yet.'}
                  </p>
                  <p className="text-sm text-slate-500 mb-4">
                    Add content in Contentful and tag it appropriately to show it here.
                  </p>
                  {activeTopic && (
                    <Button
                      variant="outline"
                      onClick={() => handleTopicChange(activeTopic)}
                      className="rounded-full"
                    >
                      Clear Filter
                    </Button>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
